import React, {useRef, useEffect} from 'react';

import View from './view';
import {useDispatch, useSelector} from 'react-redux';
import {
  holderProfileSelector,
  profileAuthenticationStateSelector,
  profileNotificationsByIdSelector,
  profileNotificationsSelector,
} from '../../redux/selectors/profile';
import {DeviceSize, useDeviceSize} from '../../shared/theme/helpers';
import {NotificationDesktop} from '../notification/desktop';
import {useModal} from '../../shared/components/modal';
import {Screens} from '../../shared/routing';
import {useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import {latestNewsSelector} from '../../redux/selectors/news';
import {NavigationRouteProps} from '../../shared/routing/model';
import {useNotificationPressHandler} from '../notification/hooks/useNotificationPressHandler';
import {getProfileNotifications} from '../../redux/actions/profile';
import {AppState, AppStateStatus} from 'react-native';
import {profileSlice} from '../../redux/reducers/profile';
import {useAccountLinker} from '../../shared/hooks/useAccountLinkerHook/useAccountLinker';
import {Platform} from 'react-native';
import {esiaCodeSelector} from '../../redux/selectors/login';
import {AppState as AppStateType} from '../../redux';
import {LoaderState} from '../../redux/reducers/loader';

const MainScreen = () => {
  const dispatch = useDispatch();
  const deviceSize = useDeviceSize();
  const profile = useSelector(holderProfileSelector());
  const esiaCode = useSelector(esiaCodeSelector);
  const notifications = useSelector(profileNotificationsSelector());
  const latestNews = useSelector(latestNewsSelector());
  const modal = useModal();
  const navigation = useNavigation();
  const route: NavigationRouteProps<{id: string}> = useRoute();
  const isModalOpened = useRef(false);
  const notificationId = +route?.params?.id;
  const notification = useSelector(profileNotificationsByIdSelector(notificationId));
  const notificationPressHandler = useNotificationPressHandler(notification, navigation);
  let lastMinimizeTime: number | null = null;
  let isAuthenticated = useSelector(profileAuthenticationStateSelector());
  const {loadLinkedAccountsInfo, linkedAccounts} = useAccountLinker();
  const isForwaredToNonActivatedLinkedAccounts = useRef(false);

  const handleFocus = React.useCallback(() => {
    dispatch(getProfileNotifications());
    isModalOpened.current = false;
  }, []);

  useFocusEffect(handleFocus);

  React.useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (Platform.OS === 'web') return;
      if (nextAppState.match(/inactive|background/)) {
        lastMinimizeTime = Date.now();
      } else if (nextAppState === 'active') {
        if (lastMinimizeTime && Date.now() - lastMinimizeTime > 1000 * 600)
          dispatch(profileSlice.actions.setAuthenticationState(false));
        lastMinimizeTime = null;
      }
    };
    const listener = AppState.addEventListener('change', handleAppStateChange);
    return () => listener?.remove();
  }, []);

  useEffect(() => {
    loadLinkedAccountsInfo();
  }, [isAuthenticated]);

  const nonActivatedStatuses = [5, 6, undefined];

  useEffect(() => {
    if (isForwaredToNonActivatedLinkedAccounts.current) return;
    if (
      isAuthenticated &&
      linkedAccounts.some((x) => x.cardNumber == null || nonActivatedStatuses.includes(x.cardStatusId))
    ) {
      isForwaredToNonActivatedLinkedAccounts.current = true;
      navigation.navigate(Platform.OS === 'web' ? Screens.DesktopProfileMe : Screens.ProfileLinkedAccounts);
    }
  }, [linkedAccounts, isAuthenticated]);

  const {showLoader} = useSelector<AppStateType, LoaderState>((state) => state.loader);

  useEffect(() => {
    if (esiaCode != null && esiaCode.code && esiaCode.state && Platform.OS === 'web' && showLoader)
      navigation.navigate(Screens.ProfileApplications);
  }, [esiaCode, showLoader]);

  React.useEffect(() => {
    if (deviceSize === DeviceSize.desktop && notificationId && !isModalOpened.current) {
      isModalOpened.current = true;

      modal.show({
        render({id: modalId}, methods) {
          const handleClose = () => {
            methods.remove(modalId);
            navigation.navigate(Screens.Main, {id: '', tab: ''});
          };
          const handlePress = notificationPressHandler
            ? () => {
                methods.clear();
                notificationPressHandler();
              }
            : null;

          return React.createElement(NotificationDesktop, {
            onModalClose: handleClose,
            onNotificationPress: handlePress,
            id: notificationId,
            navigation,
          });
        },
      });
    }

    return () => {
      if (isModalOpened.current) {
        isModalOpened.current = false;

        const lastModal = modal.getLast();
        if (lastModal) modal.remove(lastModal.id);
      }
    };
  }, [deviceSize, notificationId, notificationPressHandler]);

  return <View notifications={notifications} profile={profile} latestNews={latestNews} />;
};

export default MainScreen;
